import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, CircularProgress } from '@mui/material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import html2canvas from 'html2canvas';
import moment from 'moment';
import Snackbar from '~/util/SnackBar';
import * as Style from '../styles';

function ReportSectionCard({ title, children }) {
    const cardRef = useRef(null);
    const scrollRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const { initialDate, finalDate } = useSelector(state => state.status);

    const fileName = useMemo(() => {
        if (!initialDate || !finalDate) return title;

        const start = moment(initialDate).format('DD MMM, YYYY');
        const end = moment(finalDate).format('DD MMM, YYYY');
        const dateRange = start === end ? start : `${start} - ${end}`;

        return `${title} — ${dateRange}`;
    }, [title, initialDate, finalDate]);

    const handleDownload = useCallback(async () => {
        const card = cardRef.current;
        const scroll = scrollRef.current;
        if (!card || !scroll || downloading) return;

        setDownloading(true);

        const previousOverflow = scroll.style.overflow;
        scroll.style.overflow = 'visible';

        try {
            const fullWidth = card.scrollWidth;

            const canvas = await html2canvas(card, {
                backgroundColor: '#373435',
                scale: 2,
                width: fullWidth,
                windowWidth: fullWidth,
                ignoreElements: element =>
                    element.dataset?.exportIgnore === 'true',
            });

            const link = document.createElement('a');
            link.download = `${fileName}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            Snackbar.error('Erro ao gerar a imagem da tabela.');
        } finally {
            scroll.style.overflow = previousOverflow;
            setDownloading(false);
        }
    }, [fileName, downloading]);

    return (
        <Style.SectionCard ref={cardRef}>
            <Style.SectionHeader>
                <Style.SectionTitle>{title}</Style.SectionTitle>
                <IconButton
                    size="small"
                    aria-label="compartilhar"
                    onClick={handleDownload}
                    disabled={downloading}
                    data-export-ignore="true"
                >
                    {downloading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <ShareOutlinedIcon style={{ color: '#DCC6B7' }} />
                    )}
                </IconButton>
            </Style.SectionHeader>
            <Style.SectionScroll ref={scrollRef}>
                {children}
            </Style.SectionScroll>
        </Style.SectionCard>
    );
}

export default ReportSectionCard;
